// components/auth/MenuGuard.tsx
/**
 * Route guard that redirects to `/` (LandingRedirect) when a menu/submenu is not
 * shown to the current viewer. LandingRedirect resolves to the account's
 * landing section — a visible leaf — so this never bounces off a hidden page.
 *
 * A superadmin passes a section hidden from the account's members, as long as
 * the submenu's feature flag is on, and the page then carries the
 * "Hidden from customer" banner (`useSectionAccess`, plan
 * 2026-10-23-superadmin-sees-sections-hidden-from-the-customer). Members get
 * exactly today's rule: `menu_visibility` alone, no flag check here.
 */

import { createContext, useContext } from "react";
import { Navigate } from "@/routing/accountRouting";
import { useSectionAccess } from "@/hooks/useSectionAccess";
import { useMenuVisibility } from "@/hooks/useMenuVisibility";
import { HiddenFromCustomerBanner } from "@/components/auth/HiddenFromCustomer";

// Set by a guard that already renders the banner, so a nested guard
// (`insights` around `insights/home`) does not render a second one.
const BannerAbove = createContext(false);

type Props = {
  menu: string;
  /** One submenu, or several of which any one shown admits: the dashboard
   *  page also opens a report (ADR-0496 D1), so an account with Reports on
   *  and Dashboards off still reaches its reports there. */
  submenu?: string | string[];
  children: React.ReactNode;
};

export function MenuGuard({ menu, submenu, children }: Props) {
  const { isMenuVisible } = useMenuVisibility();
  const access = useSectionAccess();
  const bannerAbove = useContext(BannerAbove);

  const submenus = submenu === undefined ? [] : Array.isArray(submenu) ? submenu : [submenu];
  let hiddenFromCustomer: boolean;
  if (submenus.length === 0) {
    // A menu-level guard keeps the menu-key rule; the submenu guards below it
    // decide per page.
    if (isMenuVisible(menu)) hiddenFromCustomer = false;
    else if (access.isSuperadmin) hiddenFromCustomer = true;
    else return <Navigate to="/" replace />;
  } else {
    const results = submenus.map((s) => access.submenu(menu, s));
    if (!results.some((r) => r.shown)) return <Navigate to="/" replace />;
    hiddenFromCustomer = !results.some((r) => r.shown && !r.hiddenFromCustomer);
  }

  if (!hiddenFromCustomer || bannerAbove) return <>{children}</>;
  return (
    <BannerAbove.Provider value={true}>
      <HiddenFromCustomerBanner />
      {children}
    </BannerAbove.Provider>
  );
}
